<script lang="ts">
  /**
   * F18 — Homestead knows the seasons.
   *
   * Five pieces of maintenance that are fixed to a point in the Highveld year
   * rather than to an interval from the last time they were done. The screen
   * exists because the automation on its own is invisible: a rule that quietly
   * raises a job in April is indistinguishable from a rule that has stopped
   * working, and the day you need to know which is the day the firebreak did not
   * get cut.
   *
   * So every rule states its own reason in words. "Clear the gutters" is a
   * chore; "the first real storms land in mid-October and they land all at once"
   * is why the date is that date, and it is the difference between a household
   * doing it and a household snoozing it.
   *
   * `due` is the honest state and the useful one: the window is open, the job
   * has not been raised, which means the daily sweep has not run. It renders as
   * attention rather than being hidden, and the button beside it raises the job
   * by hand.
   */
  import { Icon, Panel, dateZA } from "@steyncd/steyn-design";
  import { api, ApiError } from "../lib/api";

  type State = "dormant" | "due" | "raised" | "done";
  type Rule = {
    id: string;
    title: string;
    detail: string;
    severity: "info" | "warn";
    raiseOn: string;
    dueOn: string;
    state: State;
    jobId: string | null;
  };

  let rules = $state<Rule[]>([]);
  let year = $state<number | null>(null);
  let loading = $state(true);
  let error = $state<string | null>(null);
  let busy = $state<string | null>(null);
  let notice = $state<string | null>(null);

  async function load() {
    loading = true;
    error = null;
    try {
      const res = await api.get<{ year: number; rules: Rule[] }>("/seasons");
      rules = res.rules;
      year = res.year;
    } catch (e) {
      error =
        e instanceof ApiError
          ? e.message
          : "Could not reach Homestead. This is not an empty year — try again.";
    } finally {
      loading = false;
    }
  }
  void load();

  async function raiseNow(r: Rule) {
    busy = r.id;
    notice = null;
    try {
      await api.post(`/seasons/${r.id}/raise`);
      await load();
    } catch (e) {
      notice = e instanceof ApiError ? e.message : "Could not raise that one.";
    } finally {
      busy = null;
    }
  }

  /**
   * Glyph, word AND colour for every state — never colour alone, and never
   * green. Blue is good, amber is attention, and the sentence carries the
   * meaning on its own in greyscale.
   */
  const MARK: Record<State, { icon: string; word: string; tone: "" | "ok" | "warn" }> = {
    dormant: { icon: "clock", word: "Not yet — it comes round later in the year", tone: "" },
    due: { icon: "alert", word: "Due now, and no job has been raised", tone: "warn" },
    raised: { icon: "wrench", word: "Raised — it is on the jobs list", tone: "warn" },
    done: { icon: "check", word: "Done for this year", tone: "ok" },
  };

  const attention = $derived(rules.filter((r) => r.state === "due" || r.state === "raised").length);

  /** The whole screen in one sentence, which is the home-surface rule. */
  const sentence = $derived.by(() => {
    if (rules.length === 0) return "";
    const head = `Five things the Highveld year asks for, on ${year ?? "this year"}'s dates.`;
    if (attention === 0) return `${head} Nothing wants you right now.`;
    return `${head} ${attention === 1 ? "One wants" : `${attention} want`} you.`;
  });
</script>

<!-- `sd-panel` is the selector the container query in tokens.css actually
     targets — below 560px of container width these rows stop holding --row-h and
     stack, instead of scrolling sideways on a phone. -->
<div class="wrap sd-panel">
  <header class="top">
    <h2 class="h1">The year</h2>
    <p class="lede">{sentence || "Reading the year…"}</p>
  </header>

  {#if notice}
    <p class="notice" role="status"><Icon name="alert" size={13} /> <span>{notice}</span></p>
  {/if}

  <Panel {loading} {error} isEmpty={!loading && rules.length === 0} onretry={load} variant="row" rows={5}>
    {#snippet empty()}
      <p class="empt">
        <strong>The seasons are not answering.</strong> The five Highveld rules live in the API, not in the
        database, so an empty list here means <code>homestead-api</code> is running an older revision. Deploy
        it, then reload this page.
      </p>
    {/snippet}

    <div class="list">
      {#each rules as r (r.id)}
        {@const m = MARK[r.state]}
        <article class="row" class:row--attn={m.tone === "warn"}>
          <span class="mark" class:mark--warn={m.tone === "warn"} class:mark--ok={m.tone === "ok"} aria-hidden="true">
            <Icon name={m.icon} size={18} />
          </span>

          <div class="grow">
            <h3 class="row__t">{r.title}</h3>
            <p class="row__d">{r.detail}</p>
            <p class="state" class:state--warn={m.tone === "warn"} class:state--ok={m.tone === "ok"}>
              <Icon name={m.icon} size={12} />
              {m.word}
            </p>
          </div>

          <!-- The one tight column, and only one: when it is wanted by. Everything
               else on the row breathes. -->
          <div class="when">
            <span class="when__l">Wanted by</span>
            <span class="when__v">{dateZA(r.dueOn)}</span>
            <span class="when__s">raises {dateZA(r.raiseOn)}</span>
          </div>

          <div class="acts">
            {#if r.state === "due" || r.state === "dormant"}
              <button class="btn-primary" disabled={busy === r.id} onclick={() => raiseNow(r)}>
                {busy === r.id ? "Raising…" : "Raise it now"}
              </button>
            {/if}
          </div>
        </article>
      {/each}
    </div>

    <p class="foot">
      The daily sweep raises these on their own. Anything sitting on
      <em>due now</em> means the sweep has not run — raising it by hand is safe, because a rule can only
      land once in a season.
    </p>
  </Panel>
</div>

<style>
  .wrap { display: flex; flex-direction: column; gap: var(--gap-section); }
  .top { display: flex; flex-direction: column; gap: var(--sp-2); }
  .h1 { margin: 0; font-size: var(--fs-h2); font-weight: 700; letter-spacing: -0.02em; color: var(--tx); }
  .lede { margin: 0; font-size: var(--fs-body); color: var(--tx2); }

  .notice {
    display: flex;
    align-items: center;
    gap: var(--sp-2);
    margin: 0;
    padding: var(--sp-3) var(--sp-4);
    border-left: 3px solid var(--warn);
    border-radius: var(--r-control);
    background: var(--fill);
    font-size: var(--fs-small);
    color: var(--tx);
  }

  .empt { margin: 0; }

  .list {
    border-radius: var(--r-surface);
    background: var(--s1);
    box-shadow: inset 0 0 0 1px var(--line);
    overflow: hidden;
  }
  .row {
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: var(--sp-4);
    min-height: var(--row-h);
    padding: var(--sp-4) var(--pad-x);
    border-bottom: 1px solid var(--line);
  }
  .row:last-child { border-bottom: none; }
  /* An edge as well as the hue, so the state is still there in greyscale. */
  .row--attn { border-left: 3px solid var(--warn); padding-left: calc(var(--pad-x) - 3px); }

  .mark { display: grid; place-items: center; color: var(--mut); flex-shrink: 0; padding-top: 2px; }
  .mark--warn { color: var(--warn); }
  .mark--ok { color: var(--ok); }

  .grow { flex: 1 1 18rem; min-width: 0; }
  .row__t { margin: 0; font-size: var(--fs-lead); font-weight: 700; color: var(--tx); }
  /* The reason, at body size rather than as a caption. It is the feature. */
  .row__d { margin: var(--sp-1) 0 0; font-size: var(--fs-small); color: var(--tx2); line-height: 1.55; }
  .state {
    display: inline-flex;
    align-items: center;
    gap: var(--sp-1);
    margin: var(--sp-2) 0 0;
    font-size: var(--fs-small);
    font-weight: 700;
    color: var(--mut);
  }
  .state--warn { color: var(--warn); }
  .state--ok { color: var(--ok); }

  .when { display: flex; flex-direction: column; gap: 2px; flex-shrink: 0; min-width: 9rem; }
  .when__l { font-size: var(--fs-micro); font-weight: 700; color: var(--mut); }
  .when__v { font-family: var(--font-figure); font-size: var(--fs-lead); font-weight: 700; color: var(--tx); }
  .when__s { font-size: var(--fs-micro); color: var(--mut); }

  .acts { display: flex; align-items: center; gap: var(--sp-3); flex-shrink: 0; }

  .foot { margin: 0; font-size: var(--fs-small); color: var(--mut); line-height: 1.55; }
  .foot em { font-style: normal; font-weight: 700; color: var(--tx2); }
  code { font-family: var(--font-mono); font-size: 0.95em; }
</style>
